/**
 * Number formats and value-slot reading for the reports suite (docs/reports/design.md §2).
 * Owner: slice web-charts (#1115).
 *
 * Absence is not zero (hard rule 10): a missing value formats as "—" here and the components render its state in
 * words; nothing in this module turns null into 0.
 */
import type { Cell, Chart, Column, Metric, Point, Series } from '../../../lib/api/generated'

export type Unit = Chart['unit']
export type Visibility = NonNullable<Metric['visibility']>
export type MetricState = NonNullable<Metric['state']>
export type GoodDirection = NonNullable<Metric['good']>

export const DASH = '—'

export function toNum(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

const INT = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 })
const ONE = new Intl.NumberFormat('en-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
const CAD = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', currencyDisplay: 'narrowSymbol' })
const CAD0 = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  currencyDisplay: 'narrowSymbol',
  maximumFractionDigits: 0,
})

/** Minutes as "42 min" / "3.4 h" (from 90 min) / "2.1 d" (from 36 h). */
/** A whole-number count with thousands separators. */
export function formatCount(n: number): string {
  return INT.format(n)
}

export function formatMinutes(m: number): string {
  const a = Math.abs(m)
  if (a >= 36 * 60) return `${ONE.format(m / 1440)} d`
  if (a >= 90) return `${ONE.format(m / 60)} h`
  return `${INT.format(Math.round(m))} min`
}

/** Money in CAD: cents only under $1,000. */
export function formatMoney(n: number): string {
  return (Math.abs(n) < 1000 ? CAD : CAD0).format(n)
}

/** One value in its unit. A missing value is "—" (the caller renders its state); never "0". */
export function formatValue(unit: Unit | null | undefined, v: string | number | null | undefined): string {
  if (unit === null || unit === undefined) {
    if (v === null || v === undefined || v === '') return DASH
    return typeof v === 'number' ? String(v) : v
  }
  const n = toNum(v)
  if (n === null) return DASH
  switch (unit) {
    case 'minutes':
      return formatMinutes(n)
    case 'hours':
      return `${ONE.format(n)} h`
    case 'days':
      return `${ONE.format(n)} d`
    case 'percent':
      return `${ONE.format(n)}%`
    case 'money':
      return formatMoney(n)
    case 'score':
      return ONE.format(n)
    case 'ratio':
      return n.toFixed(2)
    default:
      return Number.isInteger(n) ? INT.format(n) : ONE.format(n)
  }
}

/** Compact axis tick in the unit (1K, 2.5M; minutes on one shared scale chosen from the axis max). */
export function axisFormatter(unit: Unit, max: number): (n: number) => string {
  const compact = (n: number) => {
    const a = Math.abs(n)
    if (a >= 1_000_000) return `${trim(n / 1_000_000)}M`
    if (a >= 1000) return `${trim(n / 1000)}K`
    return trim(n)
  }
  switch (unit) {
    case 'minutes': {
      const a = Math.abs(max)
      if (a >= 36 * 60) return (n) => `${trim(n / 1440)} d`
      if (a >= 90) return (n) => `${trim(n / 60)} h`
      return (n) => `${trim(n)} min`
    }
    case 'hours':
      return (n) => `${trim(n)} h`
    case 'days':
      return (n) => `${trim(n)} d`
    case 'percent':
      return (n) => `${trim(n)}%`
    case 'money':
      return (n) => (n < 0 ? `-$${compact(-n)}` : `$${compact(n)}`)
    default:
      return compact
  }
}

function trim(n: number): string {
  const r = Math.round(n * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

type Slotted = { value?: string | null; money_value?: string | null; cost_value?: string | null }

/** The raw decimal string in the slot `visibility` names (money_value / cost_value / value). */
export function slotValue(o: Slotted, visibility: Visibility | null | undefined): string | null {
  const v = visibility === 'money' ? o.money_value : visibility === 'cost' ? o.cost_value : o.value
  return v ?? null
}

/** True when the serializer stripped this object's slot for the viewer's role (absent key, hard rule 5). */
export function slotStripped(o: Slotted, visibility: Visibility | null | undefined): boolean {
  if (visibility === 'money') return !('money_value' in o)
  if (visibility === 'cost') return !('cost_value' in o)
  return false
}

/** A metric's raw value, read from value | money_value | cost_value per its visibility. */
export function metricNumber(m: Metric): string | null {
  return slotValue(m, m.visibility)
}

export function metricPrior(m: Metric): string | null {
  const v = m.visibility === 'money' ? m.money_prior : m.visibility === 'cost' ? m.cost_prior : m.prior
  return v ?? null
}

export function pointNumber(p: Point, s: Series): number | null {
  return toNum(slotValue(p, s.visibility))
}

export function cellNumber(c: Cell, col: Column): number | null {
  return toNum(slotValue(c, col.visibility))
}

/**
 * Whether the viewer may see something with this visibility. With no `canSee` the role filter already happened at the
 * serializer, so a money/cost item whose slot is absent (stripped) is hidden rather than shown empty.
 */
export function isVisible(
  visibility: Visibility | null | undefined,
  canSee: ((v: Visibility) => boolean) | undefined,
  stripped = false,
): boolean {
  const vis = visibility ?? 'all'
  if (canSee && !canSee(vis)) return false
  return !stripped
}

export interface Delta {
  /** "up" | "down" | "flat" */
  direction: 'up' | 'down' | 'flat'
  /** "12.5%" or, for a percent metric, "2.3 pts" */
  text: string
  tone: 'good' | 'bad' | 'neutral'
}

/**
 * Change vs the prior period. Percent metrics move in points (a % of a % misleads); everything else in %.
 * Colour follows `good`, never the sign. No prior (or a zero prior for a relative change) means no delta.
 */
export function computeDelta(unit: Unit, value: string | null, prior: string | null, good: GoodDirection = 'neutral'): Delta | null {
  const v = toNum(value)
  const p = toNum(prior)
  if (v === null || p === null) return null
  let change: number
  let text: string
  if (unit === 'percent') {
    change = v - p
    text = `${ONE.format(Math.abs(change))} pts`
  } else {
    if (p === 0) return null
    change = ((v - p) / Math.abs(p)) * 100
    text = `${ONE.format(Math.abs(change))}%`
  }
  if (Math.abs(change) < 0.05) return { direction: 'flat', text: 'no change', tone: 'neutral' }
  const direction = change > 0 ? 'up' : 'down'
  const tone = good === 'neutral' ? 'neutral' : (good === 'up') === (direction === 'up') ? 'good' : 'bad'
  return { direction, text, tone }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** "2026-10-12" -> "Oct 12" (axis) or "Mon, Oct 12, 2026" (tooltip). Parsed as a calendar date, no timezone shift. */
export function formatDateKey(x: string, long = false): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(x)
  if (!m) return x
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const label = `${MONTHS[mo - 1] ?? mo} ${d}`
  if (!long) return label
  const dow = DAYS[new Date(Date.UTC(y, mo - 1, d)).getUTCDay()]
  return `${dow}, ${label}, ${y}`
}

/** Display label for an x key. */
export function xLabel(chart: Chart, x: string, long = false): string {
  if (chart.x_kind === 'category') return chart.category_labels?.[x] ?? x
  return formatDateKey(x, long)
}
